import { api } from './api'
import {
  cataloguePriceFailure,
  deriveCataloguePrice,
} from '~/lib/catalogue-price'
import { invalidSaleReporter, logNonFatal } from '~/lib/diagnostics'
import type {
  ItemType,
  ShopBootstrap,
  ShopCollectionProduct,
} from './bootstrap'
import {
  deriveCardContent,
  deriveOwnedGids,
  type OwnedGids,
} from './card-candidate'
import type { CatalogueCandidate } from './catalogue'
import { compareByDisplayOrder } from './collection-order'

// The Daily Picks assortment (GLOSSARY.md "Daily pick collection"): the
// shop-daily-pick collection rendered as Classic-style cards in
// display_order ascending (ties: entry id ascending). Contents are tree
// species or land skins; catalogue identity is (content type, gid) —
// GLOSSARY.md "Owned land skin".

export type DailyPickSource = Pick<
  ShopBootstrap,
  'collections' | 'itemTypes' | 'unlockedLandSkins' | 'unlockedTrees'
>

export type DailyPickCandidate = CatalogueCandidate

// ADR 0034: one snapshot per WebView session, including failures. Disable
// retries in both ky and Query so an unavailable guard never keeps polling.
export const dailyPickUnownedCountQuery = (userId: number | undefined) => ({
  queryKey: ['shop', 'daily-pick-unowned-count', userId] as const,
  queryFn: async () => {
    const response = await api.service
      .get(
        `store/users/${userId}/product-collections/shop-daily-pick/unowned-count`,
        { retry: 0 },
      )
      .json<{ count: number }>()
    if (!Number.isSafeInteger(response?.count) || response.count < 0) {
      throw new Error('Invalid Daily Picks unowned count')
    }
    return response.count
  },
  enabled: userId !== undefined,
  gcTime: Infinity,
  staleTime: Infinity,
  retry: false,
  retryOnMount: false,
  refetchOnMount: false,
  refetchOnReconnect: false,
  refetchOnWindowFocus: false,
})

type DailyPickOmissionReason =
  | 'invalid_collection_entry'
  | 'invalid_content'
  | 'invalid_price'
  | 'invalid_purchase_type'
  | 'missing_product'
  | 'missing_required_asset'

function reportOmission(
  reason: DailyPickOmissionReason,
  productIds: number[],
  gid?: number,
): void {
  logNonFatal('shop_daily_pick_product_omitted', {
    reason,
    product_ids: productIds.join(','),
    ...(gid === undefined ? {} : { gid: String(gid) }),
  })
}

function deriveEntryCandidate(
  entry: ShopCollectionProduct,
  itemTypes: Map<number, ItemType>,
  ownedGids: OwnedGids,
): DailyPickCandidate | null {
  const product = entry.item_type_product
  if (!product) {
    reportOmission('missing_product', [entry.item_type_product_id ?? entry.id])
    return null
  }

  // The card rules Classic and Daily Picks share (card-candidate.ts): a
  // pick without valid content or a usable land-skin icon never gets a slot.
  const result = deriveCardContent(
    product,
    itemTypes.get(product.item_type_id ?? -1),
  )
  if ('failure' in result) {
    reportOmission(result.failure, [product.id], result.identity?.gid)
    return null
  }
  const { card } = result
  const { gid } = card

  const priceFailure = cataloguePriceFailure(product)
  if (priceFailure) {
    reportOmission(priceFailure, [product.id], gid)
    return null
  }

  // Owned-or-purchaseable visibility, owned taking precedence — the Classic
  // card's rule (GLOSSARY.md "Classic product visibility"), each kind judged
  // against its own ownership list.
  const owned = ownedGids[card.content].has(gid)
  if (!owned && product.purchaseable !== true) return null

  return {
    ...card,
    owned,
    price: deriveCataloguePrice(product, invalidSaleReporter(product.id, gid)),
    product,
  }
}

// The grid's pre-hydration facts in final display order; slots never reorder
// after creation (ADR 0024's stable-slot rule).
export function deriveDailyPickCandidates(
  source: DailyPickSource,
): DailyPickCandidate[] {
  const itemTypes = new Map<number, ItemType>()
  for (const itemType of source.itemTypes) {
    if (!itemType || typeof itemType !== 'object') continue
    itemTypes.set(itemType.id, itemType)
  }
  const ownedGids = deriveOwnedGids(source)

  return source.collections[
    'shop-daily-pick'
  ].item_type_product_collection_products
    .filter((entry) => {
      if (entry && typeof entry === 'object') return true
      reportOmission('invalid_collection_entry', [])
      return false
    })
    .sort(compareByDisplayOrder)
    .flatMap((entry) => {
      const candidate = deriveEntryCandidate(entry, itemTypes, ownedGids)
      return candidate ? [candidate] : []
    })
}
